export const DEFAULT_ORDERS_COLUMN_ORDER = [
    'info', 'ordernum', 'orderedondate', 'wo', 'name', 'custponum', 'shipdate', 'lineitem',
    'orderstatus', 'availability', 'linestatusreport', 'shippingmethod',
    'country', 'comments', 'orderaccess', 'archive',
];

export const NORMAL_GROUP_ID = 'none';
export const ORDER_SET_GROUP_ID = 'order-set';
export const ORDER_SET_MERGED_ID = 'orderset';
export const ORDER_SET_SOURCE_IDS = ['ordernum', 'wo', 'name', 'custponum'];

export const MAX_CUSTOM_GROUPS = 10;

/** Columns that may be combined into a custom group (text/link fields only). */
export const GROUPABLE_COLUMN_IDS = [
    'ordernum', 'orderedondate', 'wo', 'name', 'custponum', 'shipdate', 'lineitem',
    'shippingmethod', 'country',
];

export const COLUMN_LABELS = {
    info: 'Info',
    ordernum: 'Order Number',
    orderedondate: 'Ordered On Date',
    wo: 'WO#',
    name: 'Customer Name',
    custponum: 'PO Number',
    shipdate: 'Ship Date',
    lineitem: 'Line Item',
    orderstatus: 'Order Status',
    availability: 'BOM',
    linestatusreport: 'Line Status Report',
    shippingmethod: 'Shipping Method',
    country: 'Country',
    // Column id stays 'comments' so saved column order / visibility keep working.
    comments: 'Sticky Notes',
    orderaccess: 'Order Access',
    archive: 'Archive',
    [ORDER_SET_MERGED_ID]: 'Order Set',
};

export function getColumnGroupsStorageKey(userEmail) {
    const user = (userEmail || 'anonymous').toLowerCase().trim();
    return `bridgeOrders.columnGroups.v1.${user}`;
}

export function getColumnVisibilityStorageKey(userEmail) {
    const user = (userEmail || 'anonymous').toLowerCase().trim();
    return `bridgeOrders.columnVisibility.v1.${user}`;
}

/** Load per-user column visibility map. `{ [columnId]: false }` means hidden; missing/true means visible. */
export function loadColumnVisibility(userEmail) {
    try {
        const raw = localStorage.getItem(getColumnVisibilityStorageKey(userEmail));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
        return {};
    }
}

export function saveColumnVisibility(userEmail, visibility) {
    try {
        localStorage.setItem(getColumnVisibilityStorageKey(userEmail), JSON.stringify(visibility || {}));
    } catch (e) { /* ignore storage errors */ }
}

export function getMergedIdForGroup(groupId) {
    if (groupId === ORDER_SET_GROUP_ID) return ORDER_SET_MERGED_ID;
    if (groupId && groupId.startsWith('custom-')) return `group-${groupId}`;
    return null;
}

export function getSourceIdsForGroup(groupId, customGroups = []) {
    if (groupId === ORDER_SET_GROUP_ID) return [...ORDER_SET_SOURCE_IDS];
    const custom = customGroups.find((g) => g.id === groupId);
    return custom?.columnIds ? custom.columnIds.filter((id) => id !== 'shippingcomments') : [];
}

export function getGroupDisplayName(groupId, customGroups = []) {
    if (groupId === NORMAL_GROUP_ID) return 'Normal View';
    if (groupId === ORDER_SET_GROUP_ID) return 'Order Set';
    const custom = customGroups.find((g) => g.id === groupId);
    return custom?.name || 'Custom Group';
}

export function isMergedColumnId(columnId) {
    return columnId === ORDER_SET_MERGED_ID || (typeof columnId === 'string' && columnId.startsWith('group-'));
}

export function loadColumnGroupsState(userEmail) {
    const fallback = {
        activeGroupId: NORMAL_GROUP_ID,
        defaultGroupId: NORMAL_GROUP_ID,
        customGroups: [],
    };
    try {
        const raw = localStorage.getItem(getColumnGroupsStorageKey(userEmail));
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        return {
            activeGroupId: parsed.activeGroupId || NORMAL_GROUP_ID,
            defaultGroupId: parsed.defaultGroupId || NORMAL_GROUP_ID,
            customGroups: Array.isArray(parsed.customGroups)
            ? parsed.customGroups.slice(0, MAX_CUSTOM_GROUPS).map((group) => ({
                ...group,
                columnIds: (group.columnIds || []).filter((id) => id !== 'shippingcomments'),
            }))
            : [],
        };
    } catch (e) {
        return fallback;
    }
}

export function saveColumnGroupsState(userEmail, state) {
    try {
        localStorage.setItem(getColumnGroupsStorageKey(userEmail), JSON.stringify(state));
    } catch (e) { /* ignore storage errors */ }
}

/** Replace grouped source columns with a single merged virtual column. */
export function buildEffectiveColumnOrder(baseColumnOrder, groupId, customGroups = []) {
    const order = Array.isArray(baseColumnOrder) && baseColumnOrder.length
        ? [...baseColumnOrder]
        : [...DEFAULT_ORDERS_COLUMN_ORDER];

    if (!groupId || groupId === NORMAL_GROUP_ID) {
        return order.filter((id) => DEFAULT_ORDERS_COLUMN_ORDER.includes(id) || isMergedColumnId(id))
            .map((id) => (isMergedColumnId(id) ? null : id))
            .filter(Boolean)
            .concat(DEFAULT_ORDERS_COLUMN_ORDER.filter((id) => !order.includes(id)));
    }

    const sourceIds = getSourceIdsForGroup(groupId, customGroups);
    const mergedId = getMergedIdForGroup(groupId);
    if (!sourceIds.length || !mergedId) return [...order];

    const sourceSet = new Set(sourceIds);
    const result = [];
    let mergedInserted = false;

    for (const id of order) {
        if (isMergedColumnId(id)) {
            if (id === mergedId && !mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
            continue;
        }
        if (sourceSet.has(id)) {
            if (!mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
        } else if (DEFAULT_ORDERS_COLUMN_ORDER.includes(id)) {
            result.push(id);
        }
    }

    if (!mergedInserted) {
        const insertAt = order.findIndex((id) => sourceSet.has(id));
        const idx = insertAt >= 0 ? insertAt : result.length;
        result.splice(idx, 0, mergedId);
    }

    DEFAULT_ORDERS_COLUMN_ORDER.forEach((id) => {
        if (!sourceSet.has(id) && !result.includes(id)) result.push(id);
    });

    return result;
}

/** Expand a merged column back into individual source columns (when switching to Normal View). */
export function expandMergedColumnOrder(effectiveOrder, mergedId, sourceIds) {
    if (!mergedId || !Array.isArray(effectiveOrder)) return [...DEFAULT_ORDERS_COLUMN_ORDER];
    const idx = effectiveOrder.indexOf(mergedId);
    if (idx === -1) return effectiveOrder.filter((id) => DEFAULT_ORDERS_COLUMN_ORDER.includes(id));

    const presentSources = sourceIds.filter((id) => DEFAULT_ORDERS_COLUMN_ORDER.includes(id));
    return [
        ...effectiveOrder.slice(0, idx),
        ...presentSources,
        ...effectiveOrder.slice(idx + 1).filter((id) => !isMergedColumnId(id)),
    ];
}

export function sanitizeColumnOrder(savedOrder, excludeColumnIds = []) {
    if (!Array.isArray(savedOrder)) return [...DEFAULT_ORDERS_COLUMN_ORDER];
    const excludeSet = new Set(excludeColumnIds);
    const valid = savedOrder.filter(
        (id) => (DEFAULT_ORDERS_COLUMN_ORDER.includes(id) || isMergedColumnId(id)) && !excludeSet.has(id)
    );
    const missing = DEFAULT_ORDERS_COLUMN_ORDER.filter((id) => !valid.includes(id) && !excludeSet.has(id));
    if (!valid.length) return [...DEFAULT_ORDERS_COLUMN_ORDER].filter((id) => !excludeSet.has(id));
    const next = [...valid];
    missing.forEach((id) => {
        if (id === 'orderedondate') {
            const after = next.indexOf('ordernum');
            if (after !== -1) {
                next.splice(after + 1, 0, id);
                return;
            }
        }
        next.push(id);
    });
    return next;
}

export function getEffectiveColumnOrder(columnOrder, activeGroupId, customGroups = []) {
    const baseOnly = sanitizeColumnOrder(
        (columnOrder || []).filter((id) => !isMergedColumnId(id))
    );

    if (!activeGroupId || activeGroupId === NORMAL_GROUP_ID) {
        return baseOnly;
    }

    const sourceIds = getSourceIdsForGroup(activeGroupId, customGroups);
    const mergedId = getMergedIdForGroup(activeGroupId);

    let order = (columnOrder || []).some(isMergedColumnId)
        ? sanitizeColumnOrder(columnOrder, sourceIds)
        : buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);

    if (mergedId && !order.includes(mergedId)) {
        order = buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);
    }

    return sanitizeColumnOrder(order, sourceIds);
}
